import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  MdCalendarToday,
  MdCheck,
  MdClose,
  MdRefresh,
  MdViewModule,
} from 'react-icons/md';
import { useUser } from '../context/UserContext';
import {
  consultarPanelCausas,
  consultarPanelClientesNuevos,
  consultarPanelGerencialFlujo,
  consultarPanelGerencialKpis,
  consultarPanelGerencialTendencia,
  consultarPanelOperaciones,
} from '../api/apiConfig';
import {
  fechaHoyISO,
  enrichOperations,
  moneyMM,
  MOTIVO_NUEVO_LABEL,
  prioridadConfig,
  isDummyPayload,
} from '../utils/panelOperativo';
import { filterDailySeriesBusinessDaysChile } from '../utils/feriadosChile';
import PanelSeguimientoOperaciones from '../components/PanelSeguimientoOperaciones';
import './PanelGerencial.css';

const AGING_BUCKETS = [
  { id: '0-1', label: '0 - 1 DÍA', lo: 0, hi: 1, color: '#42B84A' },
  { id: '2-3', label: '2 - 3 DÍAS', lo: 2, hi: 3, color: '#F5A800' },
  { id: '4-5', label: '4 - 5 DÍAS', lo: 4, hi: 5, color: '#F28C28' },
  { id: '6+', label: '+ 5 DÍAS', lo: 6, hi: null, color: '#EF2F32' },
];

function agingBucketId(days) {
  const n = Number(days) || 0;
  for (const b of AGING_BUCKETS) {
    if (b.hi == null) {
      if (n >= b.lo) return b.id;
    } else if (n >= b.lo && n <= b.hi) {
      return b.id;
    }
  }
  return AGING_BUCKETS[0].id;
}

function BlockError({ message }) {
  if (!message) return null;
  return <div className="pg-block-error">{message}</div>;
}

function PendingBadge() {
  return <span className="pg-pending-badge">Pendiente</span>;
}

function PendingState({ children }) {
  return <div className="pg-pending-state">{children}</div>;
}

function isPendingBlock(res) {
  return isDummyPayload(res);
}


function TrendChart({ data }) {
  const series = Array.isArray(data) && data.length ? data : [];
  if (series.length < 2) {
    return <div className="pg-small-label">Sin datos de tendencia.</div>;
  }
  const W = 720;
  const H = 220;
  const P = { l: 42, r: 18, t: 18, b: 36 };
  const rawMax = Math.max(...series.map((d) => Number(d.pendientes) || 0), 1);
  const niceStep = (() => {
    const rough = rawMax / 4;
    const pow = 10 ** Math.floor(Math.log10(rough || 1));
    const n = rough / pow;
    const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
    return step * pow;
  })();
  const max = Math.max(niceStep * 4, Math.ceil(rawMax / niceStep) * niceStep) || 1;
  const ticks = [];
  for (let v = 0; v <= max + 1e-9; v += niceStep) ticks.push(Math.round(v));
  const x = (i) => P.l + i * ((W - P.l - P.r) / (series.length - 1));
  const y = (v) => H - P.b - (v / max) * (H - P.t - P.b);
  const points = series.map((d, i) => `${x(i)},${y(Number(d.pendientes) || 0)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
      {ticks.map((v) => (
        <g key={v}>
          <line x1={P.l} y1={y(v)} x2={W - P.r} y2={y(v)} stroke="#E8EDF4" />
          <text x="8" y={y(v) + 4} fontSize="10" fill="#61708B">
            {v}
          </text>
        </g>
      ))}
      <polyline fill="none" stroke="#1268E8" strokeWidth="3" points={points} />
      {series.map((d, i) => (
        <g key={`${d.fecha}-${i}`}>
          <circle cx={x(i)} cy={y(Number(d.pendientes) || 0)} r="4.5" fill="#1268E8" />
          <text
            x={x(i)}
            y={y(Number(d.pendientes) || 0) - 11}
            textAnchor="middle"
            fontSize="10"
            fontWeight="800"
            fill="#0B2B63"
          >
            {d.pendientes}
          </text>
          <text x={x(i)} y={H - 10} textAnchor="middle" fontSize="10" fill="#61708B">
            {d.fecha}
          </text>
        </g>
      ))}
    </svg>
  );
}

const emptyErrors = () => ({
  kpis: '',
  tendencia: '',
  clientes: '',
  causas: '',
  flujo: '',
  operaciones: '',
});

const PanelGerencial = () => {
  const { user } = useUser();
  const [fecha, setFecha] = useState(fechaHoyISO);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [kpisData, setKpisData] = useState(null);
  const [tendencia, setTendencia] = useState(null);
  const [clientes, setClientes] = useState(null);
  const [causas, setCausas] = useState(null);
  const [flujo, setFlujo] = useState(null);
  const [opsData, setOpsData] = useState(null);
  const [errors, setErrors] = useState(emptyErrors);

  const load = useCallback(
    async (fechaConsulta) => {
      if (!user) return;
      setLoading(true);
      setErrors(emptyErrors());
      const opts = { fecha: fechaConsulta, vista: 'gerencial' };
      const jobs = [
        ['kpis', consultarPanelGerencialKpis(user, opts), setKpisData],
        ['tendencia', consultarPanelGerencialTendencia(user, opts), setTendencia],
        ['clientes', consultarPanelClientesNuevos(user, opts), setClientes],
        ['causas', consultarPanelCausas(user, opts), setCausas],
        ['flujo', consultarPanelGerencialFlujo(user, opts), setFlujo],
        ['operaciones', consultarPanelOperaciones(user, opts), setOpsData],
      ];
      const results = await Promise.allSettled(jobs.map((j) => j[1]));
      const nextErrors = emptyErrors();
      results.forEach((res, i) => {
        const key = jobs[i][0];
        const setter = jobs[i][2];
        if (res.status === 'fulfilled' && res.value && res.value.ok !== false) {
          setter(res.value);
        } else {
          setter(null);
          const reason = res.status === 'rejected' ? res.reason : res.value;
          nextErrors[key] =
            reason?.message || reason?.error || `No se pudo cargar el bloque ${key}.`;
        }
      });
      setErrors(nextErrors);
      setLoading(false);
    },
    [user],
  );

  useEffect(() => {
    load(fecha);
  }, [load, fecha]);

  const k = kpisData?.kpis || {};
  const kpisPending = isPendingBlock(kpisData);
  const tendenciaPending = isPendingBlock(tendencia);
  const flujoPending = isPendingBlock(flujo);
  const pagePending = kpisPending || tendenciaPending || flujoPending;
  const areasPendientes = kpisPending ? [] : k.porArea || [];
  const montoPendiente = kpisPending ? null : Number(k.montoPendienteMM) || 0;
  const prio = prioridadConfig(opsData?.config);
  const criticalThreshold = Number(opsData?.config?.criticalThresholdDays) || 3;
  const causes = causas?.causes || [];
  const causeTotal = causes.reduce((a, b) => a + (Number(b.value) || 0), 0);
  const flujoArr = filterDailySeriesBusinessDaysChile(flujo?.flujoDiario || [], {
    meta: flujo?.meta,
    fechaConsulta: fecha,
  });
  const flujoMax = Math.max(...flujoArr.flatMap((d) => [d.ingresadas, d.giradas]), 1);
  const scaleBar = (v) => Math.max(10, Math.round((Number(v) / flujoMax) * 100));
  const totalIn = flujoArr.reduce((a, b) => a + (Number(b.ingresadas) || 0), 0);
  const totalOut = flujoArr.reduce((a, b) => a + (Number(b.giradas) || 0), 0);
  const ratio = totalIn ? (totalOut / totalIn) * 100 : 0;

  const operations = useMemo(
    () =>
      enrichOperations(opsData?.operations, fecha, opsData?.config).map((o) => ({
        ...o,
        area: o.area || o.stage,
      })),
    [opsData, fecha],
  );
  const criticalOps = useMemo(
    () => operations.filter((o) => o.days > criticalThreshold),
    [operations, criticalThreshold],
  );

  const agingBuckets = useMemo(() => {
    const groups = Object.fromEntries(AGING_BUCKETS.map((b) => [b.id, []]));
    for (const o of operations) {
      const id = agingBucketId(o.days);
      groups[id].push(o);
    }
    return AGING_BUCKETS.map((b) => {
      const ops = groups[b.id] || [];
      const montoMM =
        Math.round(ops.reduce((acc, o) => acc + (Number(o.montoMM) || 0), 0) * 100) / 100;
      return {
        ...b,
        value: ops.length,
        montoMM,
        ops,
      };
    });
  }, [operations]);
  const agingMax = Math.max(...agingBuckets.map((x) => Number(x.value) || 0), 1);
  const agingTotal = agingBuckets.reduce((a, b) => a + (Number(b.value) || 0), 0);
  const agingMontoTotal =
    Math.round(agingBuckets.reduce((a, b) => a + (Number(b.montoMM) || 0), 0) * 100) / 100;
  const agingChartPending = Boolean(errors.operaciones) && !opsData;

  const AGING_ALERT_DAYS = 2;
  const agingAlertOps = useMemo(
    () => operations.filter((o) => Number(o.days) > AGING_ALERT_DAYS),
    [operations],
  );
  const agingAlertMonto = useMemo(
    () => agingAlertOps.reduce((acc, o) => acc + (Number(o.montoMM) || 0), 0),
    [agingAlertOps],
  );
  const agingAlertPct =
    montoPendiente && montoPendiente > 0 ? (agingAlertMonto / montoPendiente) * 100 : 0;

  const clientsAgg = useMemo(() => {
    const map = new Map();
    for (const o of operations) {
      const name = String(o.cliente || '').trim() || '(sin cliente)';
      const cur = map.get(name) || {
        cliente: name,
        rut: '',
        ops: 0,
        montoMM: 0,
        areas: new Set(),
      };
      cur.ops += 1;
      cur.montoMM += Number(o.montoMM) || 0;
      if (o.rut) cur.rut = o.rut;
      const area = o.area || o.stage;
      if (area) cur.areas.add(area);
      map.set(name, cur);
    }
    return Array.from(map.values())
      .map((x) => ({
        ...x,
        areas: Array.from(x.areas).join(', '),
        montoMM: Math.round(x.montoMM * 100) / 100,
      }))
      .sort((a, b) => b.montoMM - a.montoMM || b.ops - a.ops);
  }, [operations]);

  const newClientAgingOps = useMemo(() => {
    const names = new Set();
    const ruts = new Set();
    for (const x of clientes?.details || []) {
      const name = String(x.cliente || '')
        .trim()
        .toLowerCase();
      const rut = String(x.rut || '')
        .trim()
        .toLowerCase();
      if (name) names.add(name);
      if (rut) ruts.add(rut);
    }
    if (!names.size && !ruts.size) return [];
    return operations.filter((o) => {
      if (Number(o.days) <= AGING_ALERT_DAYS) return false;
      const name = String(o.cliente || '')
        .trim()
        .toLowerCase();
      const rut = String(o.rut || '')
        .trim()
        .toLowerCase();
      return (name && names.has(name)) || (rut && ruts.has(rut));
    });
  }, [operations, clientes, AGING_ALERT_DAYS]);
  const newClientAgingMonto =
    Math.round(newClientAgingOps.reduce((a, o) => a + (Number(o.montoMM) || 0), 0) * 100) / 100;

  const areaBreakdown = useMemo(() => {
    const map = new Map();
    let totalMonto = 0;
    for (const o of operations) {
      const label = String(o.area || o.stage || '').trim() || '(sin área)';
      const cur = map.get(label) || { label, ops: 0, montoMM: 0 };
      cur.ops += 1;
      cur.montoMM += Number(o.montoMM) || 0;
      map.set(label, cur);
      totalMonto += Number(o.montoMM) || 0;
    }
    const base = totalMonto > 0 ? totalMonto : Number(montoPendiente) || 0;
    return Array.from(map.values())
      .map((x) => ({
        label: x.label,
        ops: x.ops,
        montoMM: Math.round(x.montoMM * 100) / 100,
        pct: base > 0 ? (x.montoMM / base) * 100 : 0,
      }))
      .sort((a, b) => b.montoMM - a.montoMM || b.ops - a.ops);
  }, [operations, montoPendiente]);
  const areaBreakdownOps = areaBreakdown.reduce((a, r) => a + r.ops, 0);
  const areaBreakdownMonto =
    Math.round(areaBreakdown.reduce((a, r) => a + r.montoMM, 0) * 100) / 100;

  const plataformaBreakdown = useMemo(() => {
    const map = new Map();
    let totalMonto = 0;
    for (const o of operations) {
      const label = String(o.plataforma || '').trim() || '(sin plataforma)';
      const cur = map.get(label) || { label, ops: 0, montoMM: 0 };
      cur.ops += 1;
      cur.montoMM += Number(o.montoMM) || 0;
      map.set(label, cur);
      totalMonto += Number(o.montoMM) || 0;
    }
    const base = totalMonto > 0 ? totalMonto : Number(montoPendiente) || 0;
    return Array.from(map.values())
      .map((x) => ({
        label: x.label,
        ops: x.ops,
        montoMM: Math.round(x.montoMM * 100) / 100,
        pct: base > 0 ? (x.montoMM / base) * 100 : 0,
      }))
      .sort((a, b) => b.montoMM - a.montoMM || b.ops - a.ops);
  }, [operations, montoPendiente]);
  const plataformaBreakdownOps = plataformaBreakdown.reduce((a, r) => a + r.ops, 0);
  const plataformaBreakdownMonto =
    Math.round(plataformaBreakdown.reduce((a, r) => a + r.montoMM, 0) * 100) / 100;

  const patchOperation = (nOperacion, fields) => {
    const key = String(nOperacion);
    setOpsData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        operations: (prev.operations || []).map((o) =>
          String(o.nOperacion ?? '') === key ? { ...o, ...fields } : o,
        ),
      };
    });
  };

  const AREA_PLACEHOLDERS = [
    { cls: 'verification', mock: 'PENDIENTES EN VERIFICACIÓN' },
    { cls: 'cession', mock: 'PENDIENTES EN CESIÓN' },
    { cls: 'sii', mock: 'PENDIENTES EN ACEPTACIÓN SII' },
  ];
  const areaKpiCards = AREA_PLACEHOLDERS.map(({ cls, mock }, i) => {
    const area = areasPendientes[i];
    return {
      key: `area-${i}`,
      icon: <MdViewModule />,
      cls,
      pending: kpisPending,
      label: kpisPending || !area ? mock : `PENDIENTES · ${String(area.label).toUpperCase()}`,
      value: area ? area.operaciones : '—',
      sub: area ? 'operaciones en esta área (CEO)' : 'sin más áreas con pendientes',
      money: area ? moneyMM(area.montoMM) : null,
      moneyCls: cls,
      action: area && !kpisPending ? `kpi:area:${area.label}` : null,
    };
  });

  const kpiCards = [
    {
      key: 'clientes',
      icon: <MdViewModule />,
      cls: '',
      pending: kpisPending,
      label: 'TOTAL CLIENTES',
      value: kpisPending ? '—' : (k.totalClientes ?? '—'),
      sub: 'clientes con operaciones pendientes',
      action: !kpisPending ? 'kpi:clientes' : null,
    },
    ...areaKpiCards,
    {
      key: 'aging2',
      icon: <MdViewModule />,
      cls: 'blue',
      pending: Boolean(errors.operaciones) && operations.length === 0,
      label: 'OPS. CON MÁS DE 2 DÍAS',
      value: operations.length || opsData ? agingAlertOps.length : '—',
      sub: `${agingAlertOps.length} operaciones · ${agingAlertPct.toFixed(1).replace('.', ',')}% del monto pendiente`,
      money: moneyMM(agingAlertMonto),
      moneyCls: 'verification',
      action: 'kpi:aging2',
    },
    {
      key: 'fin',
      icon: <MdCheck />,
      cls: '',
      pending: kpisPending,
      label: 'OPERACIONES FINALIZADAS (HOY)',
      value: kpisPending ? '—' : (k.finalizadasHoy?.operaciones ?? '—'),
      sub: 'Tesorería · Depositada · En revisión final',
      money: !kpisPending && k.finalizadasHoy ? moneyMM(k.finalizadasHoy.montoMM) : null,
      moneyCls: 'final',
      action: !kpisPending ? 'kpi:fin' : null,
    },
    {
      key: 'monto',
      icon: <span>$</span>,
      cls: 'green',
      pending: kpisPending,
      label: 'MONTO PENDIENTE DE GIRO',
      value: kpisPending ? '—' : moneyMM(montoPendiente),
      sub: 'Pendientes (excluye Tesorería / Depositada / Revisión final)',
      compact: true,
      action: !kpisPending ? 'kpi:monto' : null,
    },
  ];

  const openKpiDetail = (action) => {
    if (!action) return;
    setModal(action);
  };

  const kpiModal = useMemo(() => {
    if (typeof modal !== 'string') return null;
    if (modal.startsWith('aging:')) {
      const id = modal.slice('aging:'.length);
      const bucket = agingBuckets.find((b) => b.id === id);
      if (!bucket) return null;
      return {
        title: `Antigüedad · ${bucket.label} (${bucket.value} ops · ${moneyMM(bucket.montoMM)})`,
        kind: 'ops',
        rows: bucket.ops,
        empty: 'Sin operaciones en este tramo de antigüedad.',
      };
    }
    if (!modal.startsWith('kpi:')) return null;
    if (modal === 'kpi:clientes') {
      return {
        title: `Clientes con pendientes (${clientsAgg.length})`,
        kind: 'clients',
        rows: clientsAgg,
        empty: 'Sin clientes con operaciones pendientes.',
      };
    }
    if (modal === 'kpi:aging2') {
      return {
        title: `Operaciones con más de ${AGING_ALERT_DAYS} días (${agingAlertOps.length})`,
        kind: 'ops',
        rows: agingAlertOps,
        empty: 'Sin operaciones con más de 2 días de antigüedad.',
      };
    }
    if (modal === 'kpi:newClientsAging') {
      return {
        title: `Clientes nuevos · ops con +${AGING_ALERT_DAYS} días (${newClientAgingOps.length})`,
        kind: 'ops',
        rows: newClientAgingOps,
        empty: 'No hay operaciones de clientes nuevos con más de 2 días de antigüedad.',
      };
    }
    if (modal === 'kpi:monto') {
      return {
        title: `Monto pendiente de giro (${operations.length} ops)`,
        kind: 'ops',
        rows: operations,
        empty: 'Sin operaciones pendientes.',
      };
    }
    if (modal === 'kpi:fin') {
      return {
        title: 'Operaciones finalizadas (hoy)',
        kind: 'ops',
        rows: [],
        empty:
          'El listado de seguimiento solo incluye pendientes. Las finalizadas del día se ven en el KPI (Tesorería / Depositada / Revisión final).',
      };
    }
    if (modal.startsWith('kpi:area:')) {
      const label = modal.slice('kpi:area:'.length);
      const rows = operations.filter(
        (o) => String(o.area || o.stage || '').trim() === String(label).trim(),
      );
      return {
        title: `Pendientes · ${label} (${rows.length})`,
        kind: 'ops',
        rows,
        empty: 'Sin operaciones en esta área.',
      };
    }
    if (modal.startsWith('kpi:plataforma:')) {
      const label = modal.slice('kpi:plataforma:'.length);
      const rows = operations.filter((o) => {
        const plat = String(o.plataforma || '').trim() || '(sin plataforma)';
        return plat === label;
      });
      return {
        title: `Plataforma · ${label} (${rows.length})`,
        kind: 'ops',
        rows,
        empty: 'Sin operaciones en esta plataforma.',
      };
    }
    return null;
  }, [modal, clientsAgg, agingAlertOps, newClientAgingOps, operations, agingBuckets]);

  return (
    <div className="pg-dash">
      <header className="pg-topbar">
        <div>
          <h1>
            Panel Gerencial{' '}
            {pagePending ? <span className="pg-wip-badge">En construcción</span> : null}
          </h1>
          <p>Visualización para Gerencia Comercial y General</p>
        </div>
        <div className="pg-toolbar">
          <label className="pg-date-box">
            <MdCalendarToday />
            <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </label>
          <button type="button" className="pg-btn" onClick={() => load(fecha)} disabled={loading}>
            <MdRefresh />
            {loading ? 'CARGANDO…' : 'ACTUALIZAR'}
          </button>
        </div>
      </header>

      <section className="pg-kpi-grid">
        {kpiCards.map((c) => {
          const clickable = Boolean(c.action) && !c.pending;
          return (
            <article
              key={c.key}
              className={`pg-kpi-card${c.pending ? ' is-pending' : ''}${clickable ? ' clickable' : ''}`}
              onClick={clickable ? () => openKpiDetail(c.action) : undefined}
              onKeyDown={
                clickable
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openKpiDetail(c.action);
                      }
                    }
                  : undefined
              }
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
            >
              <div className={`pg-kpi-icon ${c.cls}`}>{c.icon}</div>
              <div>
                <div className="pg-kpi-label">
                  {c.label}
                  {c.pending ? <PendingBadge /> : null}
                </div>
                {c.pending ? (
                  <PendingState>Aún no conectado a datos reales.</PendingState>
                ) : (
                  <>
                    <div className={`pg-kpi-value${c.compact ? ' compact' : ''}`}>{c.value}</div>
                    <div className="pg-kpi-sub">{c.sub}</div>
                    {c.money ? <div className={`pg-kpi-money ${c.moneyCls}`}>{c.money}</div> : null}
                    {clickable ? <div className="pg-kpi-link">Ver detalle →</div> : null}
                  </>
                )}
              </div>
            </article>
          );
        })}
      </section>
      <BlockError message={errors.kpis} />

      <section className="pg-grid pg-row-2">
        <article className={`pg-card${tendenciaPending ? ' is-pending' : ''}`}>
          <div className="pg-card-title">
            TENDENCIA DE PENDIENTES (CIERRE DIARIO)
            {tendenciaPending ? <PendingBadge /> : null}
          </div>
          <BlockError message={errors.tendencia} />
          {tendenciaPending ? (
            <PendingState>Falta construir el cierre diario histórico desde base_ceo.</PendingState>
          ) : (
            <div className="pg-chart-wrap">
              <TrendChart
                data={filterDailySeriesBusinessDaysChile(tendencia?.tendencia, {
                  meta: tendencia?.meta,
                  fechaConsulta: fecha,
                })}
              />
            </div>
          )}
        </article>
        <article className={`pg-card${agingChartPending ? ' is-pending' : ''}`}>
          <div className="pg-card-title">
            ANTIGÜEDAD DE PENDIENTES (OPS Y MONTO)
            {agingChartPending ? <PendingBadge /> : null}
          </div>
          <BlockError message={errors.operaciones} />
          {agingChartPending ? (
            <PendingState>Falta cargar las operaciones para armar la antigüedad.</PendingState>
          ) : (
            <>
              <div className="pg-aging-bars">
                {agingBuckets.map((x) => (
                  <button
                    key={x.id}
                    type="button"
                    className="pg-aging-item clickable"
                    onClick={() => setModal(`aging:${x.id}`)}
                    title={`Ver detalle · ${x.label}`}
                  >
                    <div className="pg-aging-label">{x.label}</div>
                    <div className="pg-aging-value">{x.value}</div>
                    <div className="pg-aging-money">{moneyMM(x.montoMM)}</div>
                    <div
                      className="pg-aging-bar"
                      style={{
                        height: `${48 + ((Number(x.value) || 0) / agingMax) * 70}px`,
                        background: x.color,
                      }}
                    />
                  </button>
                ))}
              </div>
              <div className="pg-aging-total">
                Total: {agingTotal} operaciones · {moneyMM(agingMontoTotal)}
              </div>
              <div className="pg-aging-hint">Clic en una barra para ver el detalle</div>
            </>
          )}
        </article>
        <article className="pg-card">
          <div className="pg-card-title">CLIENTES NUEVOS CON OPERACIONES PENDIENTES</div>
          <BlockError message={errors.clientes} />
          <div className="pg-new-client-block">
            <div>
              <div className="pg-big-number">{clientes?.count ?? '—'}</div>
              <div className="pg-small-label">clientes nuevos con pendientes</div>
            </div>
            <div>
              <div className="pg-money-number">{moneyMM(clientes?.montoMM)}</div>
              <div className="pg-small-label">monto pendiente</div>
            </div>
            <button
              type="button"
              className="pg-new-client-aging"
              onClick={() => setModal('kpi:newClientsAging')}
              title="Ver operaciones de clientes nuevos con más de 2 días"
            >
              <div className="pg-new-client-aging-value">{newClientAgingOps.length}</div>
              <div className="pg-small-label">ops nuevas con +2 días</div>
              <div className="pg-new-client-aging-money">{moneyMM(newClientAgingMonto)}</div>
              <div className="pg-kpi-link">Ver detalle →</div>
            </button>
          </div>
          <button type="button" className="pg-cta-link" onClick={() => setModal('newClients')}>
            VER DETALLE CLIENTES →
          </button>
        </article>
      </section>

      <section className="pg-grid pg-row-split">
        <article className="pg-card">
          <div className="pg-card-title">DESGLOSE DE PENDIENTES POR ETAPA / ÁREA</div>
          <BlockError message={errors.operaciones} />
          <div className="pg-table-wrap">
            <table className="pg-mini-table pg-stage-table">
              <thead>
                <tr>
                  <th>ETAPA</th>
                  <th>N° OPERACIONES</th>
                  <th>MONTO</th>
                  <th>% DEL MONTO</th>
                </tr>
              </thead>
              <tbody>
                {areaBreakdown.length === 0 ? (
                  <tr>
                    <td colSpan={4}>Sin operaciones pendientes</td>
                  </tr>
                ) : (
                  areaBreakdown.map((row) => (
                    <tr
                      key={row.label}
                      className="pg-stage-row"
                      onClick={() => setModal(`kpi:area:${row.label}`)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setModal(`kpi:area:${row.label}`);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      title={`Ver detalle · ${row.label}`}
                    >
                      <td className="pg-stage-name">{row.label}</td>
                      <td>{row.ops}</td>
                      <td>{moneyMM(row.montoMM)}</td>
                      <td>{row.pct.toFixed(1).replace('.', ',')}%</td>
                    </tr>
                  ))
                )}
              </tbody>
              {areaBreakdown.length > 0 ? (
                <tfoot>
                  <tr>
                    <td>Total</td>
                    <td>{areaBreakdownOps}</td>
                    <td>{moneyMM(areaBreakdownMonto)}</td>
                    <td>100%</td>
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>
          <div className="pg-aging-hint">Clic en una etapa para ver el detalle</div>
        </article>

        <article className="pg-card">
          <div className="pg-card-title">RESUMEN DE PENDIENTES POR PLATAFORMA</div>
          <BlockError message={errors.operaciones} />
          <div className="pg-table-wrap">
            <table className="pg-mini-table pg-stage-table">
              <thead>
                <tr>
                  <th>PLATAFORMA</th>
                  <th>N° OPERACIONES</th>
                  <th>MONTO</th>
                  <th>% DEL TOTAL</th>
                </tr>
              </thead>
              <tbody>
                {plataformaBreakdown.length === 0 ? (
                  <tr>
                    <td colSpan={4}>Sin operaciones pendientes</td>
                  </tr>
                ) : (
                  plataformaBreakdown.map((row) => (
                    <tr
                      key={row.label}
                      className="pg-stage-row"
                      onClick={() => setModal(`kpi:plataforma:${row.label}`)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setModal(`kpi:plataforma:${row.label}`);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      title={`Ver detalle · ${row.label}`}
                    >
                      <td className="pg-stage-name">{row.label}</td>
                      <td>{row.ops}</td>
                      <td>{moneyMM(row.montoMM)}</td>
                      <td>{row.pct.toFixed(1).replace('.', ',')}%</td>
                    </tr>
                  ))
                )}
              </tbody>
              {plataformaBreakdown.length > 0 ? (
                <tfoot>
                  <tr>
                    <td>TOTAL</td>
                    <td>{plataformaBreakdownOps}</td>
                    <td>{moneyMM(plataformaBreakdownMonto)}</td>
                    <td>100%</td>
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>
          <div className="pg-aging-hint">Clic en una plataforma para ver el detalle</div>
        </article>
      </section>

      <section className="pg-grid pg-row-3">
        <article className={`pg-card${isPendingBlock(causas) ? ' is-pending' : ''}`}>
          <div className="pg-card-title">
            PRINCIPALES CAUSAS DE DETENCIÓN (DEL DÍA)
            {isPendingBlock(causas) ? <PendingBadge /> : null}
          </div>
          <BlockError message={errors.causas} />
          {isPendingBlock(causas) ? (
            <PendingState>Este bloque aún no está conectado a datos reales.</PendingState>
          ) : causes.length === 0 ? (
            <div className="pg-small-label">No hay detenciones (cuadrados) en esta fecha.</div>
          ) : (
            causes.map((c) => (
            <div key={c.label} className="pg-cause-row">
              <div>{c.label}</div>
              <div className="pg-track">
                <div
                  className="pg-fill"
                  style={{
                    width: `${Math.min(100, Number(c.pct) || 0)}%`,
                    background: c.color,
                  }}
                />
              </div>
              <strong>{c.value}</strong>
              <span>({c.pct}%)</span>
            </div>
            ))
          )}
          {isPendingBlock(causas) ? null : (
            <div className="pg-foot-total">Total: {causeTotal} operaciones del día</div>
          )}
        </article>
        <article className={`pg-card${flujoPending ? ' is-pending' : ''}`}>
          <div className="pg-card-title">
            OPERACIONES INGRESADAS VS. OPERACIONES GIRADAS POR DÍA
            {flujoPending ? <PendingBadge /> : null}
          </div>
          <BlockError message={errors.flujo} />
          {flujoPending ? (
            <PendingState>
              Falta definir qué cuenta como girada e historial de 7 días.
            </PendingState>
          ) : (
            <div className="pg-throughput-layout">
              <div className="pg-throughput-chart">
                {flujoArr.map((d) => (
                  <div key={d.fecha} className="pg-day-group">
                    <div className="pg-bar-wrap">
                      <div className="pg-bar in" style={{ height: `${scaleBar(d.ingresadas)}px` }}>
                        <span className="pg-bar-label">{d.ingresadas}</span>
                      </div>
                      <div className="pg-bar out" style={{ height: `${scaleBar(d.giradas)}px` }}>
                        <span className="pg-bar-label">{d.giradas}</span>
                      </div>
                    </div>
                    <div className="pg-day-label">{d.fecha}</div>
                  </div>
                ))}
              </div>
              <div className="pg-throughput-summary">
                <div>
                  <div className="pg-summary-label">TOTAL 7 DÍAS</div>
                </div>
                <div>
                  <div className="pg-summary-label">Ingresadas</div>
                  <div className="pg-summary-value blue">{totalIn}</div>
                </div>
                <div>
                  <div className="pg-summary-label">Giradas</div>
                  <div className="pg-summary-value green">{totalOut}</div>
                </div>
                <div>
                  <div className="pg-summary-label">Ratio salida / ingreso</div>
                  <div className="pg-summary-value">{ratio.toFixed(1).replace('.', ',')}%</div>
                </div>
              </div>
            </div>
          )}
        </article>
        <article className="pg-card">
          <div className="pg-card-title">OPERACIONES CRÍTICAS (MÁS DE {criticalThreshold} DÍAS EN ESPERA)</div>
          <BlockError message={errors.operaciones} />
          <div className="pg-table-wrap">
            <table className="pg-mini-table">
              <thead>
                <tr>
                  <th>CLIENTE</th>
                  <th>MONTO (MM)</th>
                  <th>ETAPA ACTUAL</th>
                  <th>DÍAS EN ESPERA</th>
                </tr>
              </thead>
              <tbody>
                {criticalOps.slice(0, 5).length === 0 ? (
                  <tr>
                    <td colSpan={4}>Sin operaciones críticas</td>
                  </tr>
                ) : (
                  criticalOps.slice(0, 5).map((o) => (
                    <tr key={o.id ?? `${o.nOperacion || 'op'}-${o.createdAt}`}>
                      <td>{o.cliente}</td>
                      <td>$ {o.montoMM}</td>
                      <td>{o.stage}</td>
                      <td className="pg-critical-days">{o.days}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <button
            type="button"
            className="pg-cta-link pg-cta-inline"
            onClick={() => setModal('criticalOps')}
            disabled={criticalOps.length === 0}
          >
            VER TODAS LAS OPERACIONES CRÍTICAS →
          </button>
        </article>
      </section>

      <PanelSeguimientoOperaciones
        operations={operations}
        loading={loading}
        error={errors.operaciones}
        fecha={fecha}
        user={user}
        exportFileName={`panel_gerencial_${fecha}.xlsx`}
        onPatch={patchOperation}
        prioridad={prio}
        hideColumns={['gestionador', 'asignadoEn', 'comentario']}
        allowEditGestion={false}
        columnLabels={{
          td: 'TIPO DOCUMENTO',
          area: 'ETAPA ACTUAL',
          days: 'DÍAS DE ESPERA (HÁBILES)',
          updatedAt: 'ÚLTIMA ACTUALIZACIÓN',
        }}
        columnWidthsKey="mgc.panelGerencial.opsColWidths.v1"
      />

      {modal === 'newClients' ? (
        <div
          className="pg-modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) setModal(null);
          }}
        >
          <div className="pg-modal">
            <div className="pg-modal-header">
              <h2>Clientes nuevos con operaciones pendientes</h2>
              <button type="button" className="pg-modal-close" onClick={() => setModal(null)}>
                <MdClose />
              </button>
            </div>
            <table className="pg-detail-table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>RUT</th>
                  <th>Ops. históricas</th>
                  <th>Última operación</th>
                  <th>Monto pendiente</th>
                  <th>Motivo</th>
                  <th>Área</th>
                </tr>
              </thead>
              <tbody>
                {(clientes?.details || []).map((x, idx) => (
                  <tr key={`${x.cliente}-${x.rut || idx}`}>
                    <td>{x.cliente}</td>
                    <td>{x.rut || '—'}</td>
                    <td>{x.nOps ?? x.n_ops ?? '—'}</td>
                    <td>{x.ultimaOperacion || x.ultima_operacion || '—'}</td>
                    <td>$ {x.montoMM} MM</td>
                    <td>{MOTIVO_NUEVO_LABEL[x.motivo] || x.motivo || '—'}</td>
                    <td>{x.area || x.stage || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {kpiModal ? (
        <div
          className="pg-modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) setModal(null);
          }}
        >
          <div className="pg-modal">
            <div className="pg-modal-header">
              <h2>{kpiModal.title}</h2>
              <button type="button" className="pg-modal-close" onClick={() => setModal(null)}>
                <MdClose />
              </button>
            </div>
            <table className="pg-detail-table">
              {kpiModal.kind === 'clients' ? (
                <>
                  <thead>
                    <tr>
                      <th>Cliente</th>
                      <th>RUT</th>
                      <th>Operaciones</th>
                      <th>Monto (MM)</th>
                      <th>Áreas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {kpiModal.rows.length === 0 ? (
                      <tr>
                        <td colSpan={5}>{kpiModal.empty}</td>
                      </tr>
                    ) : (
                      kpiModal.rows.map((x) => (
                        <tr key={`${x.cliente}-${x.rut}`}>
                          <td>{x.cliente}</td>
                          <td>{x.rut || '—'}</td>
                          <td>{x.ops}</td>
                          <td>$ {x.montoMM}</td>
                          <td>{x.areas || '—'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </>
              ) : (
                <>
                  <thead>
                    <tr>
                      <th>Cliente</th>
                      <th>RUT</th>
                      <th>N° operación</th>
                      <th>Ejecutivo</th>
                      <th>Área</th>
                      <th>Monto (MM)</th>
                      <th>Días en espera</th>
                      <th>Prioridad</th>
                      <th>Última actualización</th>
                    </tr>
                  </thead>
                  <tbody>
                    {kpiModal.rows.length === 0 ? (
                      <tr>
                        <td colSpan={9}>{kpiModal.empty}</td>
                      </tr>
                    ) : (
                      kpiModal.rows.map((o) => (
                        <tr key={o.id ?? `${o.nOperacion || 'op'}-${o.createdAt}`}>
                          <td>{o.cliente || '—'}</td>
                          <td>{o.rut || '—'}</td>
                          <td>{o.nOperacion ?? '—'}</td>
                          <td>{o.ejecutivo || '—'}</td>
                          <td>{o.area || o.stage || '—'}</td>
                          <td>$ {o.montoMM}</td>
                          <td className="pg-critical-days">{o.days}</td>
                          <td>{o.priority || '—'}</td>
                          <td>{o.updatedAt || '—'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </>
              )}
            </table>
          </div>
        </div>
      ) : null}

      {modal === 'criticalOps' ? (
        <div
          className="pg-modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) setModal(null);
          }}
        >
          <div className="pg-modal">
            <div className="pg-modal-header">
              <h2>
                Operaciones críticas ({criticalOps.length}) — más de {criticalThreshold} días
                hábiles
              </h2>
              <button type="button" className="pg-modal-close" onClick={() => setModal(null)}>
                <MdClose />
              </button>
            </div>
            <table className="pg-detail-table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>RUT</th>
                  <th>N° operación</th>
                  <th>Ejecutivo</th>
                  <th>Área</th>
                  <th>Monto (MM)</th>
                  <th>Días en espera</th>
                  <th>Prioridad</th>
                  <th>Última actualización</th>
                </tr>
              </thead>
              <tbody>
                {criticalOps.length === 0 ? (
                  <tr>
                    <td colSpan={9}>Sin operaciones críticas</td>
                  </tr>
                ) : (
                  criticalOps.map((o) => (
                    <tr key={o.id ?? `${o.nOperacion || 'op'}-${o.createdAt}`}>
                      <td>{o.cliente || '—'}</td>
                      <td>{o.rut || '—'}</td>
                      <td>{o.nOperacion ?? '—'}</td>
                      <td>{o.ejecutivo || '—'}</td>
                      <td>{o.area || o.stage || '—'}</td>
                      <td>$ {o.montoMM}</td>
                      <td className="pg-critical-days">{o.days}</td>
                      <td>{o.priority || '—'}</td>
                      <td>{o.updatedAt || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PanelGerencial;
